import { useCallback } from 'react';
import { MixpanelEvents } from 'integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'integrations/mixpanel/useMixpanel';
import { Notification } from 'providers/Notifications/Notifications.types';
import { useLazyQuery } from '@apollo/client';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { NotificationTypeModel } from '@utmost.co/enterprise-models';
import { downloadMediaOrLink } from 'src/utils/media/downloadMediaOrLink';
import { useFileName } from 'src/utils/media/downloadMedia';
import { UseExportQueryTotalCountDocument } from 'src/components/pages/InsightReports/Export/utils/__generated__/useExportQuery.graphql';

export const useNotificationDownload = () => {
  const { track } = useMixpanel();
  const getFileName = useFileName();
  const { showError } = useSnackbar();
  const [getExportTotalCount] = useLazyQuery<{ exports: { totalElements: number } }>(UseExportQueryTotalCountDocument);

  const reportEvent = useCallback(
    (exportType: NotificationTypeModel, source: 'recent-exports' | 'notifications') => {
      track(
        source === 'recent-exports'
          ? MixpanelEvents.myContentExport.DOWNLOAD_EXPORT_FROM_RECENT_EXPORTS
          : MixpanelEvents.notification.DOWNLOAD_FROM_NOTIFICATION,
        { exportType }
      );
      getExportTotalCount()
        .then(({ data }) => {
          if (data?.exports.totalElements !== undefined) {
            track(MixpanelEvents.myContentExport.LIST_EXPORTS, { totalCount: data.exports.totalElements });
          }
        })
        .catch(() => {});
    },
    [track, getExportTotalCount]
  );

  const onDownload = (notification: DeepMaybe<Notification>, source: 'recent-exports' | 'notifications') => {
    if (notification?.resourceLocation) {
      downloadMediaOrLink(
        notification,
        getFileName({
          extension: notification.type === 'CSV_EXPORT' ? 'csv' : 'xlsx',
          prefix: notification.metadata?.find((e) => e?.key === 'NAME')?.value || '',
          requestedTime: notification.created ?? '',
          entityType: notification.metadata?.find((e) => e?.key === 'ENTITY_TYPE')?.value || ''
        }),
        showError
      );
      notification.type && reportEvent(notification.type, source);
    }
  };

  return onDownload;
};
